import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Switch,
  TextInput,
  ActivityIndicator,
  StyleSheet,
  Platform,
  FlatList,
} from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { Ionicons } from '@expo/vector-icons';
import * as Clipboard from 'expo-clipboard';
import { ScreenLayout, Header, FormHeader, ContactSelector } from '../components';
import { ProductSelectorModal } from '../components/catalogues/ProductSelectorModal';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { apiService } from '../services/api';
import { ZoneMapPicker, ZoneResult } from '../components/ZoneMapPicker';

// ─── Confirm modal ────────────────────────────────────────────────────────────

const ConfirmModal: React.FC<{
  visible: boolean; title: string; message: string;
  onConfirm: () => void; onCancel: () => void;
}> = ({ visible, title, message, onConfirm, onCancel }) => {
  const cm = useThemedStyles(getCm);
  if (!visible) return null;
  const content = (
    <View style={cm.box}>
      <Text style={cm.title}>{title}</Text>
      <Text style={cm.message}>{message}</Text>
      <View style={cm.btnRow}>
        <TouchableOpacity style={cm.cancelBtn} onPress={onCancel} activeOpacity={0.7}>
          <Text style={cm.cancelText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity style={cm.deleteBtn} onPress={onConfirm} activeOpacity={0.7}>
          <Text style={cm.deleteText}>Confirm</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <View style={cm.overlay}>
        <TouchableOpacity style={cm.backdrop} activeOpacity={1} onPress={onCancel} />
        {content}
      </View>
    </Modal>
  );
};
const getCm = () => StyleSheet.create({
  overlay: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.45)' },
  box: { width: 300, backgroundColor: colors.background.surface, borderRadius: 14, padding: 24, gap: 12, elevation: 10, shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 12, shadowOffset: { width: 0, height: 4 } },
  title: { fontSize: 16, fontWeight: '700', color: '#111', textAlign: 'center' },
  message: { fontSize: 14, color: '#555', textAlign: 'center', lineHeight: 20 },
  btnRow: { flexDirection: 'row', gap: 10, marginTop: 4 },
  cancelBtn: { flex: 1, borderRadius: 8, borderWidth: 1, borderColor: '#ddd', paddingVertical: 10, alignItems: 'center' },
  cancelText: { fontSize: 14, color: '#555', fontWeight: '500' },
  deleteBtn: { flex: 1, borderRadius: 8, backgroundColor: '#E53E3E', paddingVertical: 10, alignItems: 'center' },
  deleteText: { fontSize: 14, color: '#fff', fontWeight: '600' },
});

// ─── MultiPicker ─────────────────────────────────────────────────────────────

interface NamedItem { id: string; name: string; }

const MultiPicker: React.FC<{
  visible: boolean; title: string; selected: NamedItem[]; items: NamedItem[];
  loading: boolean; search: string;
  onSearchChange: (q: string) => void; onClose: () => void; onConfirm: (items: NamedItem[]) => void;
}> = ({ visible, title, selected, items, loading, search, onSearchChange, onClose, onConfirm }) => {
  const mp = useThemedStyles(getMp);
  const s = useThemedStyles(getS);
  const [picked, setPicked] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (visible) { const m = new Map<string, string>(); selected.forEach(s => m.set(s.id, s.name)); setPicked(m); }
  }, [visible]);
  const toggle = (item: NamedItem) => setPicked(prev => {
    const next = new Map(prev);
    if (next.has(item.id)) next.delete(item.id); else next.set(item.id, item.name);
    return next;
  });
  const confirm = () => { onConfirm(Array.from(picked.entries()).map(([id, name]) => ({ id, name }))); onClose(); };
  const content = (
    <View style={{ flex: 1, backgroundColor: colors.background.surface }}>
      <View style={mp.header}>
        <Text style={mp.title}>{title}</Text>
        <TouchableOpacity onPress={onClose} activeOpacity={0.7}><Ionicons name="close" size={24} color={colors.textLight} /></TouchableOpacity>
      </View>
      <View style={mp.searchBox}>
        <Ionicons name="search" size={18} color={colors.textLight} />
        <TextInput style={mp.searchInput} value={search} onChangeText={onSearchChange} placeholder="Search…" placeholderTextColor={colors.textLight} />
        {search.length > 0 && <TouchableOpacity onPress={() => onSearchChange('')} activeOpacity={0.7}><Ionicons name="close-circle" size={18} color={colors.textLight} /></TouchableOpacity>}
      </View>
      {loading ? <View style={mp.center}><ActivityIndicator color={colors.primary} /></View> : (
        <FlatList data={items} keyExtractor={i => i.id} renderItem={({ item }) => {
          const sel = picked.has(item.id);
          return (
            <TouchableOpacity style={mp.row} onPress={() => toggle(item)} activeOpacity={0.7}>
              <View style={[mp.checkbox, sel && mp.checkboxChecked]}>{sel && <Ionicons name="checkmark" size={14} color={colors.white} />}</View>
              <Text style={mp.itemName}>{item.name}</Text>
            </TouchableOpacity>
          );
        }} ListEmptyComponent={<View style={mp.center}><Text style={{ color: colors.textLight }}>No results</Text></View>} />
      )}
      <View style={mp.footer}>
        <Text style={{ color: colors.textSecondary, fontSize: typography.fontSize.sm }}>{picked.size} selected</Text>
        <TouchableOpacity style={mp.confirmBtn} onPress={confirm} activeOpacity={0.8}><Text style={mp.confirmText}>Confirm</Text></TouchableOpacity>
      </View>
    </View>
  );
  if (Platform.OS === 'web') {
    if (!visible) return null;
    return (
      <View style={mp.webOverlay}>
        <TouchableOpacity style={mp.webBackdrop} activeOpacity={1} onPress={onClose} />
        <View style={mp.webSheet}>{content}</View>
      </View>
    );
  }
  return <Modal visible={visible} animationType="slide" presentationStyle="formSheet">{content}</Modal>;
};
const getMp = () => StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  title: { fontSize: typography.fontSize.lg, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  searchBox: { flexDirection: 'row', alignItems: 'center', margin: spacing.md, borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, gap: spacing.xs },
  searchInput: { flex: 1, paddingVertical: spacing.sm, fontSize: typography.fontSize.base, color: colors.textDark },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, gap: spacing.sm, borderBottomWidth: 1, borderBottomColor: colors.border + '50' },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: colors.border, alignItems: 'center', justifyContent: 'center' },
  checkboxChecked: { backgroundColor: colors.primary, borderColor: colors.primary },
  itemName: { fontSize: typography.fontSize.base, color: colors.textDark },
  footer: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: spacing.md, borderTopWidth: 1, borderTopColor: colors.border },
  confirmBtn: { backgroundColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.lg, paddingVertical: spacing.sm },
  confirmText: { color: colors.white, fontWeight: '600', fontFamily: typography.fontFamily.semibold },
  webOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 10 },
  webBackdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.5)' },
  webSheet: { position: 'absolute' as any, top: spacing.xl * 2, left: spacing.xl * 3, right: spacing.xl * 3, bottom: spacing.xl * 2, backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, overflow: 'hidden', ...designTokens.shadows.lg },
});

// ─── TagRow ───────────────────────────────────────────────────────────────────

const TagRow: React.FC<{ items: NamedItem[]; onRemove: (id: string) => void }> = ({ items, onRemove }) => {
  const tag = useThemedStyles(getTag);
  if (!items.length) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginTop: spacing.xs }}>
      {items.map(item => (
        <View key={item.id} style={tag.pill}>
          <Text style={tag.label}>{item.name}</Text>
          <TouchableOpacity onPress={() => onRemove(item.id)} activeOpacity={0.7} style={{ marginLeft: 4 }}>
            <Ionicons name="close-circle" size={14} color={colors.primary} />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );
};
const getTag = () => StyleSheet.create({
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary + '15', borderRadius: 20, paddingHorizontal: spacing.sm, paddingVertical: 3, borderWidth: 1, borderColor: colors.primary + '40' },
  label: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
});

// ─── TargetingSection ─────────────────────────────────────────────────────────

interface TargetingState { contacts: NamedItem[]; products: NamedItem[]; catalogues: NamedItem[]; }
const emptyTargeting = (): TargetingState => ({ contacts: [], products: [], catalogues: [] });

const TargetingSection: React.FC<{ value: TargetingState; onChange: (v: TargetingState) => void; label?: string }> = ({ value, onChange, label }) => {
  const s = useThemedStyles(getS);
  const [pickerContact, setPickerContact] = useState<any>(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [showCataloguePicker, setShowCataloguePicker] = useState(false);
  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [catalogueItems, setCatalogueItems] = useState<NamedItem[]>([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const catTimer = useRef<any>(null);

  const loadCatalogues = async (q: string) => {
    setCatalogueLoading(true);
    try {
      const res = await apiService.getCatalogues({ search: q || undefined, limit: 50 });
      if (res.statusCode === 1000) setCatalogueItems((res.data?.catalogues ?? []).map((c: any) => ({ id: c._id, name: c.name })));
    } catch (_) {} finally { setCatalogueLoading(false); }
  };

  const addContact = (contact: any) => {
    const id = contact._id ?? contact.id ?? '';
    const name = contact.name ?? contact.fullName ?? id;
    if (!id || value.contacts.some(c => c.id === id)) return;
    onChange({ ...value, contacts: [...value.contacts, { id, name }] });
    setPickerContact(null);
  };

  return (
    <View style={{ gap: spacing.sm }}>
      {label && <Text style={s.sectionSubLabel}>{label}</Text>}
      <View style={s.field}>
        <Text style={s.fieldLabel}>Specific Contacts (empty = all)</Text>
        <TagRow items={value.contacts} onRemove={id => onChange({ ...value, contacts: value.contacts.filter(c => c.id !== id) })} />
        <Text style={[s.hint, { marginTop: spacing.xs }]}>Search and select to add:</Text>
        <ContactSelector mode="edit" variant="simple" contact={pickerContact} onContactSelect={addContact} />
      </View>
      <View style={s.field}>
        <Text style={s.fieldLabel}>Specific Products (empty = all)</Text>
        <TagRow items={value.products} onRemove={id => onChange({ ...value, products: value.products.filter(p => p.id !== id) })} />
        <TouchableOpacity style={s.pickerBtn} onPress={() => setShowProductPicker(true)} activeOpacity={0.7}>
          <Ionicons name="cube-outline" size={16} color={colors.primary} />
          <Text style={s.pickerBtnText}>{value.products.length ? 'Edit Products' : 'Add Products'}</Text>
        </TouchableOpacity>
      </View>
      <View style={s.field}>
        <Text style={s.fieldLabel}>Catalogues (empty = all)</Text>
        <TagRow items={value.catalogues} onRemove={id => onChange({ ...value, catalogues: value.catalogues.filter(c => c.id !== id) })} />
        <TouchableOpacity style={s.pickerBtn} onPress={() => { setCatalogueSearch(''); loadCatalogues(''); setShowCataloguePicker(true); }} activeOpacity={0.7}>
          <Ionicons name="albums-outline" size={16} color={colors.primary} />
          <Text style={s.pickerBtnText}>{value.catalogues.length ? 'Edit Catalogues' : 'Add Catalogues'}</Text>
        </TouchableOpacity>
      </View>
      <ProductSelectorModal
        visible={showProductPicker} onClose={() => setShowProductPicker(false)}
        title="Select Products" confirmText="Add Selected" allowMultiple
        onConfirm={async (selectedIds) => {
          setShowProductPicker(false);
          const existing = new Map(value.products.map(p => [p.id, p.name]));
          const newIds = selectedIds.filter(id => !existing.has(id));
          const resolved = await Promise.all(newIds.map(async id => {
            try { const res = await apiService.getProductById(id); return { id, name: res.statusCode === 1000 ? (res.data?.name ?? id) : id }; }
            catch { return { id, name: id }; }
          }));
          const selectedSet = new Set(selectedIds);
          onChange({ ...value, products: [...value.products.filter(p => selectedSet.has(p.id)), ...resolved] });
        }}
      />
      <MultiPicker
        visible={showCataloguePicker} title="Select Catalogues"
        selected={value.catalogues} items={catalogueItems} loading={catalogueLoading}
        search={catalogueSearch}
        onSearchChange={q => { setCatalogueSearch(q); clearTimeout(catTimer.current); catTimer.current = setTimeout(() => loadCatalogues(q), 350); }}
        onClose={() => setShowCataloguePicker(false)}
        onConfirm={items => onChange({ ...value, catalogues: items })}
      />
    </View>
  );
};

// ─── Types ────────────────────────────────────────────────────────────────────

const PRODUCT_TYPES = ['physical', 'digital', 'service'] as const;
type ProductType = typeof PRODUCT_TYPES[number];

interface ConfigZone {
  name: string; mode?: 'country' | 'city' | 'radius';
  cities?: string[]; polygon?: [number, number][]; polygons?: [number, number][][];
  center?: { latitude: number; longitude: number }; radiusKm?: number;
  value?: number; type?: 'percentage' | 'flat'; enabled?: boolean;
}

interface ReferralForm {
  enabled: boolean; rewardType: 'percentage' | 'flat';
  rewardValue: string; referrerRewardValue: string;
  minOrderAmount: string; expiryDays: string;
  firstTimeOnly: boolean; appliesTo: ProductType[];
  targeting: TargetingState; zones: ConfigZone[];
}

type ZonePickerTarget =
  | { rule: 'program'; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' }
  | { rule: 'code'; zoneIndex: number | null; mode: 'country' | 'city' | 'radius' };

// ─── Screen ───────────────────────────────────────────────────────────────────

export const ReferralsScreen: React.FC = () => {
  const s = useThemedStyles(getS);
  const zs = useThemedStyles(getZs);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [showLeftPane] = useState(Platform.OS === 'web');
  const [showRightPane] = useState(Platform.OS === 'web');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [confirmModal, setConfirmModal] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);
  const confirmAction = (title: string, message: string, onConfirm: () => void) => setConfirmModal({ title, message, onConfirm });

  // ── Program settings ──────────────────────────────────────────────────────
  const [referral, setReferral] = useState<ReferralForm>({
    enabled: false, rewardType: 'flat',
    rewardValue: '0', referrerRewardValue: '0',
    minOrderAmount: '', expiryDays: '',
    firstTimeOnly: false, appliesTo: [],
    targeting: emptyTargeting(), zones: [],
  });

  // ── Referral codes ────────────────────────────────────────────────────────
  const [referralCodes, setReferralCodes] = useState<any[]>([]);
  const [referralCodesLoading, setReferralCodesLoading] = useState(false);
  const [newCodeLabel, setNewCodeLabel] = useState('');
  const [newCodeValue, setNewCodeValue] = useState('');
  const [addingCode, setAddingCode] = useState(false);
  const [codesSaving, setCodesSaving] = useState(false);
  const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);
  const [redemptions, setRedemptions] = useState<Record<string, any[]>>({});
  const [redemptionActionLoading, setRedemptionActionLoading] = useState<string | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState<string | null>(null);

  // ── Per-code override editor ──────────────────────────────────────────────
  const [editingCodeId, setEditingCodeId] = useState<string | null>(null);
  const [codeOverride, setCodeOverride] = useState<any>(null);
  const [codeOverrideSaving, setCodeOverrideSaving] = useState(false);

  // ── Zone picker ───────────────────────────────────────────────────────────
  const [zonePicker, setZonePicker] = useState<ZonePickerTarget | null>(null);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const resolveProductNames = async (ids: string[]): Promise<NamedItem[]> =>
    Promise.all(ids.map(async id => {
      try { const res = await apiService.getProductById(id); return { id, name: res.statusCode === 1000 ? (res.data?.name ?? id) : id }; }
      catch { return { id, name: id }; }
    }));

  const resolveCatalogueNames = async (ids: string[]): Promise<NamedItem[]> => {
    try {
      const res = await apiService.getCatalogues({ limit: 200 });
      if (res.statusCode === 1000) {
        const map = new Map((res.data?.catalogues ?? []).map((c: any) => [c._id, c.name]));
        return ids.map(id => ({ id, name: (map.get(id) as string) ?? id }));
      }
    } catch (_) {}
    return ids.map(id => ({ id, name: id }));
  };

  const resolveContactNames = async (ids: string[]): Promise<NamedItem[]> => {
    try {
      const res = await apiService.getUnifiedContacts({ limit: 200, offset: 0 });
      if (res.statusCode === 1000) {
        const all: any[] = res.data?.contacts ?? res.data ?? [];
        const map = new Map(all.map((c: any) => [c._id ?? c.id, c.name ?? c.fullName]));
        return ids.map(id => ({ id, name: (map.get(id) as string) ?? id }));
      }
    } catch (_) {}
    return ids.map(id => ({ id, name: id }));
  };

  // ── Load config ───────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBusinessConfig();
      if (res.statusCode === 1000 && res.data?.referral) {
        const r = res.data.referral;
        const contacts = r.eligibleContactIds?.length ? await resolveContactNames(r.eligibleContactIds) : [];
        const products = r.eligibleProductIds?.length ? await resolveProductNames(r.eligibleProductIds) : [];
        const catalogues = r.eligibleCatalogueIds?.length ? await resolveCatalogueNames(r.eligibleCatalogueIds) : [];
        setReferral({
          enabled: r.enabled ?? false,
          rewardType: r.rewardType ?? 'flat',
          rewardValue: String(r.rewardValue ?? 0),
          referrerRewardValue: String(r.referrerRewardValue ?? 0),
          minOrderAmount: r.minOrderAmount != null ? String(r.minOrderAmount) : '',
          expiryDays: r.expiryDays != null ? String(r.expiryDays) : '',
          firstTimeOnly: r.firstTimeOnly ?? false,
          appliesTo: r.appliesTo ?? [],
          targeting: { contacts, products, catalogues },
          zones: r.zones ?? [],
        });
      }
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setLoading(false); }
  }, [showNotification]);

  const fetchReferralCodes = useCallback(async () => {
    setReferralCodesLoading(true);
    try {
      const res = await apiService.listReferrals();
      if (res.statusCode === 1000) setReferralCodes((res.data as any) || []);
    } catch {} finally { setReferralCodesLoading(false); }
  }, []);

  useEffect(() => { loadConfig(); fetchReferralCodes(); }, []);

  // ── Save program settings ─────────────────────────────────────────────────

  const saveReferral = async () => {
    setSaving(true);
    try {
      const res = await apiService.patchBusinessConfigReferral({
        enabled: referral.enabled,
        rewardType: referral.rewardType,
        rewardValue: parseFloat(referral.rewardValue) || 0,
        referrerRewardValue: parseFloat(referral.referrerRewardValue) || 0,
        minOrderAmount: referral.minOrderAmount ? parseFloat(referral.minOrderAmount) : undefined,
        expiryDays: referral.expiryDays ? parseInt(referral.expiryDays) : undefined,
        firstTimeOnly: referral.firstTimeOnly,
        appliesTo: referral.appliesTo,
        eligibleContactIds: referral.targeting.contacts.map(c => c.id),
        eligibleProductIds: referral.targeting.products.map(p => p.id),
        eligibleCatalogueIds: referral.targeting.catalogues.map(c => c.id),
        zones: referral.zones,
      });
      if (res.statusCode === 1000) showNotification('success', 'Saved', 'Referral settings updated');
      else showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to save');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setSaving(false); }
  };

  // ── Codes CRUD ────────────────────────────────────────────────────────────

  const handleCreateCode = async () => {
    if (!newCodeLabel.trim()) { showNotification('error', 'Error', 'Label is required'); return; }
    setCodesSaving(true);
    try {
      const res = await apiService.createReferralCode(newCodeLabel.trim(), newCodeValue.trim() || undefined);
      if (res.statusCode === 1000) {
        setReferralCodes(prev => [res.data, ...prev]);
        setNewCodeLabel(''); setNewCodeValue(''); setAddingCode(false);
        showNotification('success', 'Created', `Code ${res.data.code} created`);
      } else {
        showNotification('error', 'Error', (res.errors?.[0] as any)?.message || String(res.errors?.[0] || 'Failed'));
      }
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setCodesSaving(false); }
  };

  const handleDeleteCode = (id: string, code: string) => {
    confirmAction('Delete Code', `Remove referral code "${code}"? Past redemptions will be preserved.`, async () => {
      setConfirmModal(null);
      try {
        const res = await apiService.deleteReferralCode(id);
        if (res.statusCode === 1000) {
          setReferralCodes(prev => prev.filter((c: any) => c._id !== id));
          showNotification('success', 'Deleted', 'Code removed');
        } else showNotification('error', 'Error', 'Failed to delete');
      } catch (e: any) { showNotification('error', 'Error', e.message); }
    });
  };

  const handleExpandCode = async (id: string) => {
    if (expandedCodeId === id) { setExpandedCodeId(null); return; }
    setExpandedCodeId(id);
    if (redemptions[id]) return;
    try {
      const res = await apiService.getReferralRedemptions(id);
      if (res.statusCode === 1000) setRedemptions(prev => ({ ...prev, [id]: (res.data as any) || [] }));
    } catch {}
  };

  // ── Per-code override ─────────────────────────────────────────────────────

  const handleOpenCodeOverride = (rc: any) => {
    const ov = rc.override || {};
    setCodeOverride({
      rewardType: ov.rewardType || 'flat',
      rewardValue: ov.rewardValue != null ? String(ov.rewardValue) : '',
      referrerRewardValue: ov.referrerRewardValue != null ? String(ov.referrerRewardValue) : '',
      minOrderAmount: ov.minOrderAmount != null ? String(ov.minOrderAmount) : '',
      firstTimeOnly: ov.firstTimeOnly || false,
      appliesTo: ov.appliesTo || [],
      targeting: {
        contacts: (ov.eligibleContactIds || []).map((id: string) => ({ id, name: id })),
        products: (ov.eligibleProductIds || []).map((id: string) => ({ id, name: id })),
        catalogues: (ov.eligibleCatalogueIds || []).map((id: string) => ({ id, name: id })),
      },
      zones: ov.zones || [],
    });
    setEditingCodeId(rc._id);
  };

  const handleSaveCodeOverride = async () => {
    if (!editingCodeId || !codeOverride) return;
    setCodeOverrideSaving(true);
    try {
      const hasRewardOverride = codeOverride.rewardValue !== '' || codeOverride.referrerRewardValue !== '';
      const override = {
        ...(hasRewardOverride ? {
          rewardType: codeOverride.rewardType,
          rewardValue: codeOverride.rewardValue !== '' ? parseFloat(codeOverride.rewardValue) : undefined,
          referrerRewardValue: codeOverride.referrerRewardValue !== '' ? parseFloat(codeOverride.referrerRewardValue) : undefined,
        } : {}),
        minOrderAmount: codeOverride.minOrderAmount !== '' ? parseFloat(codeOverride.minOrderAmount) : undefined,
        firstTimeOnly: codeOverride.firstTimeOnly || undefined,
        appliesTo: codeOverride.appliesTo.length ? codeOverride.appliesTo : undefined,
        eligibleContactIds: codeOverride.targeting.contacts.map((c: any) => c.id),
        eligibleProductIds: codeOverride.targeting.products.map((p: any) => p.id),
        eligibleCatalogueIds: codeOverride.targeting.catalogues.map((c: any) => c.id),
        zones: codeOverride.zones.length ? codeOverride.zones : undefined,
      };
      const res = await apiService.updateReferralCode(editingCodeId, { override });
      if (res.statusCode === 1000) {
        setReferralCodes(prev => prev.map((c: any) => c._id === editingCodeId ? { ...c, override } : c));
        setEditingCodeId(null); setCodeOverride(null);
        showNotification('success', 'Saved', 'Code settings updated');
      } else showNotification('error', 'Error', (res.errors?.[0] as any)?.message || 'Failed to save');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setCodeOverrideSaving(false); }
  };

  // ── Redemption actions ────────────────────────────────────────────────────

  const handleRedemptionAction = async (redemptionId: string, referralId: string, rewardStatus: 'paid' | 'pending' | 'cancelled') => {
    setRedemptionActionLoading(redemptionId);
    try {
      const res = await apiService.updateRedemptionStatus(redemptionId, rewardStatus);
      if (res.statusCode === 1000) {
        setRedemptions(prev => ({
          ...prev,
          [referralId]: (prev[referralId] || []).map((r: any) => r._id === redemptionId ? { ...r, rewardStatus } : r),
        }));
        showNotification('success', 'Updated', rewardStatus === 'paid' ? 'Reward marked as paid' : 'Reward cancelled');
      } else showNotification('error', 'Error', 'Failed to update');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setRedemptionActionLoading(null); }
  };

  const handleBulkRedemptionAction = async (referralId: string, rewardStatus: 'paid' | 'cancelled') => {
    setBulkActionLoading(referralId + rewardStatus);
    try {
      const res = await apiService.bulkUpdateRedemptionStatus(referralId, rewardStatus);
      if (res.statusCode === 1000) {
        setRedemptions(prev => ({
          ...prev,
          [referralId]: (prev[referralId] || []).map((r: any) => r.rewardStatus === 'pending' ? { ...r, rewardStatus } : r),
        }));
        showNotification('success', 'Updated', `All pending rewards ${rewardStatus === 'paid' ? 'marked as paid' : 'cancelled'}`);
      } else showNotification('error', 'Error', 'Failed to update');
    } catch (e: any) { showNotification('error', 'Error', e.message); }
    finally { setBulkActionLoading(null); }
  };

  // ── Zone helpers ──────────────────────────────────────────────────────────

  const zoneResultToConfigZone = (result: ZoneResult, existing?: ConfigZone): ConfigZone => {
    const geo: Partial<ConfigZone> =
      result.mode === 'city' || result.mode === 'country'
        ? { mode: result.mode, cities: [result.cityName], center: result.center, polygon: result.polygon }
        : { mode: 'radius', center: result.center, radiusKm: result.radiusKm };
    return { ...geo, name: result.name, value: existing?.value, type: existing?.type, enabled: existing?.enabled };
  };

  const handleZoneConfirm = (result: ZoneResult) => {
    if (!zonePicker) return;
    if (zonePicker.rule === 'program') {
      setReferral(prev => {
        const zones = [...prev.zones];
        const existing = zonePicker.zoneIndex !== null ? zones[zonePicker.zoneIndex!] : undefined;
        const zone = zoneResultToConfigZone(result, existing);
        if (zonePicker.zoneIndex !== null) zones[zonePicker.zoneIndex!] = zone; else zones.push(zone);
        return { ...prev, zones };
      });
    } else {
      setCodeOverride((prev: any) => {
        const zones = [...(prev?.zones || [])];
        const existing = zonePicker.zoneIndex !== null ? zones[zonePicker.zoneIndex!] : undefined;
        const zone = zoneResultToConfigZone(result, existing);
        if (zonePicker.zoneIndex !== null) zones[zonePicker.zoneIndex!] = zone; else zones.push(zone);
        return { ...prev, zones };
      });
    }
    setZonePicker(null);
  };

  const renderZoneSection = (
    zones: ConfigZone[],
    onZonesChange: (z: ConfigZone[]) => void,
    openPicker: (zoneIndex: number | null, mode: 'country' | 'city' | 'radius') => void,
    valueLabel: string,
    parentType?: 'percentage' | 'flat',
  ) => (
    <View style={{ gap: spacing.sm }}>
      <View style={[s.row, { justifyContent: 'space-between' }]}>
        <Text style={s.sectionSubLabel}>Location Pricing</Text>
        <Text style={s.hint}>First matching zone wins</Text>
      </View>
      {zones.map((zone, zi) => (
        <View key={zi} style={zs.zoneCard}>
          <View style={[s.row, { justifyContent: 'space-between' }]}>
            <View style={{ flex: 1, gap: 2 }}>
              <Text style={zs.zoneName}>{zone.name}</Text>
              <Text style={zs.zoneMeta}>{zone.mode === 'radius' ? `Radius ${zone.radiusKm ?? '?'} km` : zone.cities?.join(', ') ?? zone.mode ?? ''}</Text>
            </View>
            <TouchableOpacity onPress={() => openPicker(zi, zone.mode ?? 'city')} style={s.iconBtn} activeOpacity={0.7}>
              <Ionicons name="map-outline" size={16} color={colors.primary} />
            </TouchableOpacity>
            <TouchableOpacity onPress={() => onZonesChange(zones.filter((_, i) => i !== zi))} style={s.iconBtn} activeOpacity={0.7}>
              <Ionicons name="trash-outline" size={16} color={colors.error} />
            </TouchableOpacity>
          </View>
          <View style={s.row}>
            <View style={s.field}>
              <Text style={s.fieldLabel}>{valueLabel} override (blank = default)</Text>
              <TextInput
                style={[s.input, { width: 110 }]}
                value={zone.value != null ? String(zone.value) : ''}
                onChangeText={v => { const next = [...zones]; next[zi] = { ...next[zi], value: v === '' ? undefined : parseFloat(v) || 0 }; onZonesChange(next); }}
                keyboardType="decimal-pad" placeholder="Default" placeholderTextColor={colors.textLight}
              />
            </View>
            {parentType !== undefined && (
              <View style={[s.field, { flex: 1 }]}>
                <Text style={s.fieldLabel}>Type override</Text>
                <View style={s.row}>
                  {(['flat', 'percentage'] as const).map(tp => (
                    <TouchableOpacity key={tp} style={[s.typeBtn, (zone.type ?? parentType) === tp && s.typeBtnActive]}
                      onPress={() => { const next = [...zones]; next[zi] = { ...next[zi], type: tp === parentType ? undefined : tp }; onZonesChange(next); }}
                      activeOpacity={0.7}>
                      <Text style={[s.typeBtnText, (zone.type ?? parentType) === tp && s.typeBtnTextActive]}>{tp === 'flat' ? 'Flat' : '%'}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            )}
            <View style={[s.field, { alignItems: 'center' }]}>
              <Text style={s.fieldLabel}>Disable</Text>
              <Switch value={zone.enabled === false}
                onValueChange={v => { const next = [...zones]; next[zi] = { ...next[zi], enabled: v ? false : undefined }; onZonesChange(next); }}
                trackColor={{ true: colors.error }} />
            </View>
          </View>
        </View>
      ))}
      <View style={zs.addZoneRow}>
        {(['country', 'city', 'radius'] as const).map(mode => (
          <TouchableOpacity key={mode} style={zs.addZoneBtn} onPress={() => openPicker(null, mode)} activeOpacity={0.7}>
            <Ionicons name={mode === 'country' ? 'globe-outline' : mode === 'city' ? 'location-outline' : 'radio-button-on-outline'} size={14} color={colors.primary} />
            <Text style={zs.addZoneBtnText}>{mode === 'country' ? 'Country' : mode === 'city' ? 'City' : 'Radius'}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ── Initial zone data for picker ──────────────────────────────────────────

  const zonePickerInitialData = (() => {
    if (!zonePicker || zonePicker.zoneIndex === null) return undefined;
    const zone = zonePicker.rule === 'program'
      ? referral.zones[zonePicker.zoneIndex!]
      : codeOverride?.zones?.[zonePicker.zoneIndex!];
    if (!zone) return undefined;
    if (zone.mode === 'radius') return { mode: 'radius' as const, name: zone.name, price: zone.value ?? 0, center: zone.center, radiusKm: zone.radiusKm };
    return { mode: (zone.mode ?? 'city') as 'city' | 'country', name: zone.name, price: zone.value ?? 0, cityName: zone.cities?.[0] ?? '', center: zone.center };
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      showLeftPane={showLeftPane} onLeftPaneClose={() => {}} leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      showRightPane={showRightPane} onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Referral Program" onBack={() => navigateTo('settings')} />}
    >
      {loading ? (
        <View style={s.center}><ActivityIndicator size="large" color={colors.primary} /></View>
      ) : (
        <ScrollView style={s.scroll} contentContainerStyle={s.scrollContent} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>

          {/* ── Referral Codes ──────────────────────────────────────────── */}
          <View style={s.card}>
            <View style={[s.row, { marginBottom: 4 }]}>
              <View style={{ flex: 1 }}>
                <Text style={s.sectionSubLabel}>Referral Codes</Text>
                <Text style={s.hint}>Create codes for marketers. Track usage and rewards per code.</Text>
              </View>
              <TouchableOpacity
                style={{ backgroundColor: colors.primary, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6, marginLeft: 8 }}
                onPress={() => setAddingCode(v => !v)} activeOpacity={0.8}>
                <Ionicons name={addingCode ? 'close' : 'add'} size={18} color="#fff" />
              </TouchableOpacity>
            </View>

            {addingCode && (
              <View style={{ backgroundColor: '#F0F7FF', borderRadius: 10, padding: 12, marginBottom: 12, gap: 8 }}>
                <View style={s.field}>
                  <Text style={s.fieldLabel}>Marketer / Label *</Text>
                  <TextInput style={s.input} value={newCodeLabel} onChangeText={setNewCodeLabel}
                    placeholder="e.g. Ahmed - Instagram" placeholderTextColor={colors.textLight} />
                </View>
                <View style={s.field}>
                  <Text style={s.fieldLabel}>Custom Code (optional, auto-generated if empty)</Text>
                  <TextInput style={s.input} value={newCodeValue} onChangeText={v => setNewCodeValue(v.toUpperCase())}
                    placeholder="e.g. AHMED20" placeholderTextColor={colors.textLight} autoCapitalize="characters" />
                </View>
                <TouchableOpacity
                  style={{ backgroundColor: colors.primary, borderRadius: 8, paddingVertical: 10, alignItems: 'center' }}
                  onPress={handleCreateCode} disabled={codesSaving} activeOpacity={0.8}>
                  {codesSaving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>Create Code</Text>}
                </TouchableOpacity>
              </View>
            )}

            {referralCodesLoading ? (
              <ActivityIndicator color={colors.primary} style={{ marginVertical: 16 }} />
            ) : referralCodes.length === 0 ? (
              <Text style={[s.hint, { textAlign: 'center', paddingVertical: 16 }]}>No referral codes yet. Tap + to create one.</Text>
            ) : (
              referralCodes.map((rc: any) => {
                const isExpanded = expandedCodeId === rc._id;
                const codeRedemptions: any[] = redemptions[rc._id] || [];
                return (
                  <View key={rc._id} style={{ borderWidth: 1, borderColor: '#e8e8e8', borderRadius: 10, marginBottom: 8, overflow: 'hidden' }}>
                    {/* Code row header */}
                    <TouchableOpacity
                      style={{ flexDirection: 'row', alignItems: 'center', padding: 12, backgroundColor: isExpanded ? '#F0F7FF' : '#fff' }}
                      onPress={() => handleExpandCode(rc._id)} activeOpacity={0.7}>
                      <View style={{ flex: 1 }}>
                        <Text style={{ fontWeight: '700', fontSize: 14, color: '#1a1a1a' }}>{rc.label || '—'}</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3 }}>
                          <TouchableOpacity
                            style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#E8F4FD', borderRadius: 6, paddingHorizontal: 8, paddingVertical: 2 }}
                            onPress={async () => {
                              if (Platform.OS === 'web' && navigator.clipboard) {
                                await navigator.clipboard.writeText(rc.code);
                              } else {
                                await Clipboard.setStringAsync(rc.code);
                              }
                              showNotification('success', 'Copied', `${rc.code} copied to clipboard`);
                            }}
                            activeOpacity={0.7}>
                            <Text style={{ fontSize: 12, fontWeight: '700', color: colors.primary, letterSpacing: 1 }}>{rc.code}</Text>
                            <Ionicons name="copy-outline" size={12} color={colors.primary} />
                          </TouchableOpacity>
                          <Text style={{ fontSize: 12, color: '#888' }}>
                            {rc.redemptionCount || 0} uses · {rc.totalRewards > 0 ? `${rc.totalRewards.toFixed(2)} rewards` : 'no rewards yet'}
                          </Text>
                        </View>
                      </View>
                      <TouchableOpacity onPress={() => handleDeleteCode(rc._id, rc.code)} style={{ padding: 6, marginRight: 4 }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                        <Ionicons name="trash-outline" size={16} color="#E53E3E" />
                      </TouchableOpacity>
                      <Ionicons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={16} color="#888" />
                    </TouchableOpacity>

                    {/* Expanded content */}
                    {isExpanded && (
                      <View style={{ borderTopWidth: 1, borderTopColor: colors.border, backgroundColor: colors.background.secondary, padding: 12, gap: 12 }}>

                        {/* Settings toggle */}
                        <TouchableOpacity
                          style={{ flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: colors.primary, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 7, alignSelf: 'flex-start' }}
                          onPress={() => editingCodeId === rc._id ? (setEditingCodeId(null), setCodeOverride(null)) : handleOpenCodeOverride(rc)}
                          activeOpacity={0.7}>
                          <Ionicons name={editingCodeId === rc._id ? 'close-outline' : 'settings-outline'} size={15} color={colors.primary} />
                          <Text style={{ fontSize: 13, color: colors.primary, fontWeight: '600' }}>
                            {editingCodeId === rc._id ? 'Close Settings' : 'Code Settings'}
                          </Text>
                        </TouchableOpacity>

                        {/* Per-code override editor */}
                        {editingCodeId === rc._id && codeOverride && (
                          <View style={{ backgroundColor: colors.background.surface, borderRadius: 10, borderWidth: 1, borderColor: colors.border, padding: 12, gap: 12 }}>
                            <Text style={[s.sectionSubLabel, { color: colors.primary }]}>Override Settings</Text>
                            <Text style={s.hint}>Leave blank to inherit from Program Settings.</Text>

                            <View style={s.field}>
                              <Text style={s.fieldLabel}>Reward Type Override</Text>
                              <View style={s.chipRow}>
                                {(['flat', 'percentage'] as const).map(type => (
                                  <TouchableOpacity key={type} style={[s.chip, codeOverride.rewardType === type && s.chipActive]}
                                    onPress={() => setCodeOverride((p: any) => ({ ...p, rewardType: type }))} activeOpacity={0.7}>
                                    <Text style={[s.chipText, codeOverride.rewardType === type && s.chipTextActive]}>
                                      {type === 'flat' ? 'Fixed Amount' : 'Percentage'}
                                    </Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            </View>

                            <View style={s.row}>
                              <View style={[s.field, { flex: 1 }]}>
                                <Text style={s.fieldLabel}>Customer Discount Override</Text>
                                <TextInput style={s.input} value={codeOverride.rewardValue}
                                  onChangeText={v => setCodeOverride((p: any) => ({ ...p, rewardValue: v }))}
                                  keyboardType="decimal-pad" placeholder="Global default" placeholderTextColor={colors.textLight} />
                              </View>
                              <View style={[s.field, { flex: 1 }]}>
                                <Text style={s.fieldLabel}>Referrer Reward Override</Text>
                                <TextInput style={s.input} value={codeOverride.referrerRewardValue}
                                  onChangeText={v => setCodeOverride((p: any) => ({ ...p, referrerRewardValue: v }))}
                                  keyboardType="decimal-pad" placeholder="Global default" placeholderTextColor={colors.textLight} />
                              </View>
                            </View>

                            <View style={s.field}>
                              <Text style={s.fieldLabel}>Min Order Amount Override</Text>
                              <TextInput style={s.input} value={codeOverride.minOrderAmount}
                                onChangeText={v => setCodeOverride((p: any) => ({ ...p, minOrderAmount: v }))}
                                keyboardType="decimal-pad" placeholder="Global default" placeholderTextColor={colors.textLight} />
                            </View>

                            <View style={s.divider} />

                            <View style={s.row}>
                              <View style={{ flex: 1 }}>
                                <Text style={s.rowLabel}>First-Time Customers Only</Text>
                              </View>
                              <Switch value={codeOverride.firstTimeOnly}
                                onValueChange={v => setCodeOverride((p: any) => ({ ...p, firstTimeOnly: v }))}
                                trackColor={{ true: colors.primary }} />
                            </View>

                            <View style={s.field}>
                              <Text style={s.fieldLabel}>Applies To Product Types (empty = all)</Text>
                              <View style={s.chipRow}>
                                {PRODUCT_TYPES.map(type => (
                                  <TouchableOpacity key={type}
                                    style={[s.chip, codeOverride.appliesTo.includes(type) && s.chipActive]}
                                    onPress={() => setCodeOverride((p: any) => ({
                                      ...p,
                                      appliesTo: p.appliesTo.includes(type) ? p.appliesTo.filter((x: string) => x !== type) : [...p.appliesTo, type],
                                    }))}
                                    activeOpacity={0.7}>
                                    <Text style={[s.chipText, codeOverride.appliesTo.includes(type) && s.chipTextActive]}>
                                      {type.charAt(0).toUpperCase() + type.slice(1)}
                                    </Text>
                                  </TouchableOpacity>
                                ))}
                              </View>
                            </View>

                            <View style={s.divider} />

                            <TargetingSection
                              label="Eligible Contacts / Products / Catalogues"
                              value={codeOverride.targeting}
                              onChange={(targeting: TargetingState) => setCodeOverride((p: any) => ({ ...p, targeting }))}
                            />

                            <View style={s.divider} />

                            {renderZoneSection(
                              codeOverride.zones,
                              (zones: ConfigZone[]) => setCodeOverride((p: any) => ({ ...p, zones })),
                              (zoneIndex, mode) => setZonePicker({ rule: 'code', zoneIndex, mode }),
                              codeOverride.rewardType === 'percentage' ? 'Reward (%)' : 'Reward Amount',
                              codeOverride.rewardType,
                            )}

                            <View style={s.row}>
                              <TouchableOpacity style={[s.outlineBtn, { flex: 1 }]}
                                onPress={() => { setEditingCodeId(null); setCodeOverride(null); }} activeOpacity={0.7}>
                                <Text style={s.outlineBtnText}>Cancel</Text>
                              </TouchableOpacity>
                              <TouchableOpacity style={[s.saveBtn, { flex: 2 }]}
                                onPress={handleSaveCodeOverride} disabled={codeOverrideSaving} activeOpacity={0.8}>
                                {codeOverrideSaving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={s.saveBtnText}>Save Code Settings</Text>}
                              </TouchableOpacity>
                            </View>
                          </View>
                        )}

                        {/* Redemptions */}
                        <View>
                          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                            <Text style={[s.sectionSubLabel, { flex: 1 }]}>Redemptions</Text>
                            {codeRedemptions.some((r: any) => r.rewardStatus === 'pending') && (
                              <View style={{ flexDirection: 'row', gap: 6 }}>
                                <TouchableOpacity
                                  style={{ backgroundColor: '#2e7d32', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}
                                  onPress={() => handleBulkRedemptionAction(rc._id, 'paid')}
                                  disabled={bulkActionLoading === rc._id + 'paid'} activeOpacity={0.8}>
                                  {bulkActionLoading === rc._id + 'paid'
                                    ? <ActivityIndicator color="#fff" size="small" />
                                    : <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>Mark All Paid</Text>}
                                </TouchableOpacity>
                                <TouchableOpacity
                                  style={{ backgroundColor: '#E53E3E', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5 }}
                                  onPress={() => handleBulkRedemptionAction(rc._id, 'cancelled')}
                                  disabled={bulkActionLoading === rc._id + 'cancelled'} activeOpacity={0.8}>
                                  {bulkActionLoading === rc._id + 'cancelled'
                                    ? <ActivityIndicator color="#fff" size="small" />
                                    : <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>Cancel All</Text>}
                                </TouchableOpacity>
                              </View>
                            )}
                          </View>

                          {codeRedemptions.length === 0 ? (
                            <Text style={[s.hint, { textAlign: 'center', paddingVertical: 8 }]}>No redemptions yet</Text>
                          ) : (
                            <>
                              {codeRedemptions.map((r: any, i: number) => {
                                const isDelivered = r.orderStatus === 'delivered';
                                const rewardStatus: string = r.rewardStatus || 'pending';
                                const isLoadingThis = redemptionActionLoading === r._id;
                                return (
                                  <View key={r._id || i} style={{ borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: 10, marginBottom: 8, gap: 6, backgroundColor: colors.background.surface }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                      <Text style={{ fontSize: 13, fontWeight: '700', color: '#1a1a1a' }}>
                                        {r.orderNumber || r.orderId?.slice(-8) || '—'}
                                      </Text>
                                      {isDelivered && (
                                        <View style={{ backgroundColor: '#E8F5E9', borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 }}>
                                          <Text style={{ fontSize: 10, fontWeight: '700', color: '#2e7d32' }}>DELIVERED · ELIGIBLE</Text>
                                        </View>
                                      )}
                                      <View style={{
                                        borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2,
                                        backgroundColor: rewardStatus === 'paid' ? '#E3F2FD' : rewardStatus === 'cancelled' ? '#FDECEA' : '#FFF8E1',
                                      }}>
                                        <Text style={{
                                          fontSize: 10, fontWeight: '700',
                                          color: rewardStatus === 'paid' ? colors.primary : rewardStatus === 'cancelled' ? '#E53E3E' : '#F57F17',
                                        }}>
                                          {rewardStatus.toUpperCase()}
                                        </Text>
                                      </View>
                                      <TouchableOpacity
                                        onPress={() => navigateTo('order-edit', r.orderId)}
                                        style={{ marginLeft: 'auto' as any }} activeOpacity={0.7}>
                                        <Text style={{ fontSize: 11, color: colors.primary, fontWeight: '600' }}>View Order →</Text>
                                      </TouchableOpacity>
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 16 }}>
                                      <Text style={{ fontSize: 12, color: '#555' }}>
                                        Discount: <Text style={{ color: '#2e7d32', fontWeight: '600' }}>{(r.referredDiscount || 0).toFixed(2)}</Text>
                                      </Text>
                                      <Text style={{ fontSize: 12, color: '#555' }}>
                                        Reward: <Text style={{ color: colors.primary, fontWeight: '600' }}>{(r.referrerReward || 0).toFixed(2)}</Text>
                                      </Text>
                                    </View>
                                    <View style={{ flexDirection: 'row', gap: 6, marginTop: 2, justifyContent: 'flex-end' }}>
                                      {isLoadingThis ? (
                                        <ActivityIndicator color={colors.primary} size="small" style={{ padding: 6 }} />
                                      ) : rewardStatus === 'pending' ? (
                                        <>
                                          <TouchableOpacity
                                            style={{ borderRadius: 6, backgroundColor: '#E8F5E9', padding: 6 }}
                                            onPress={() => handleRedemptionAction(r._id, rc._id, 'paid')}
                                            activeOpacity={0.8}>
                                            <Ionicons name="checkmark-circle-outline" size={18} color="#2e7d32" />
                                          </TouchableOpacity>
                                          <TouchableOpacity
                                            style={{ borderRadius: 6, backgroundColor: '#FDECEA', padding: 6 }}
                                            onPress={() => handleRedemptionAction(r._id, rc._id, 'cancelled')}
                                            activeOpacity={0.8}>
                                            <Ionicons name="close-circle-outline" size={18} color="#E53E3E" />
                                          </TouchableOpacity>
                                        </>
                                      ) : rewardStatus === 'cancelled' ? (
                                        <TouchableOpacity
                                          style={{ borderRadius: 6, backgroundColor: '#FFF8E1', padding: 6 }}
                                          onPress={() => handleRedemptionAction(r._id, rc._id, 'pending')}
                                          activeOpacity={0.8}>
                                          <Ionicons name="refresh-outline" size={18} color="#F57F17" />
                                        </TouchableOpacity>
                                      ) : null}
                                    </View>
                                  </View>
                                );
                              })}
                              <View style={{ flexDirection: 'row', paddingTop: 8, marginTop: 2, borderTopWidth: 1, borderTopColor: '#ddd' }}>
                                <Text style={{ flex: 1, fontSize: 12, fontWeight: '700', color: '#1a1a1a' }}>Total</Text>
                                <Text style={{ fontSize: 12, color: '#555' }}>
                                  Discounts: <Text style={{ color: '#2e7d32', fontWeight: '700' }}>{codeRedemptions.reduce((sum: number, r: any) => sum + (r.referredDiscount || 0), 0).toFixed(2)}</Text>
                                </Text>
                                <Text style={{ fontSize: 12, color: '#555', marginLeft: 12 }}>
                                  Rewards: <Text style={{ color: colors.primary, fontWeight: '700' }}>{codeRedemptions.reduce((sum: number, r: any) => sum + (r.referrerReward || 0), 0).toFixed(2)}</Text>
                                </Text>
                              </View>
                            </>
                          )}
                        </View>

                      </View>
                    )}
                  </View>
                );
              })
            )}
          </View>

          {/* ── Program Settings ────────────────────────────────────────── */}
          <View style={s.card}>
            <Text style={s.sectionSubLabel}>Program Settings</Text>
            <Text style={[s.hint, { marginBottom: 4 }]}>Define the default rewards applied when a customer uses a referral code at checkout.</Text>
            <View style={s.row}>
              <Text style={s.rowLabel}>Enable Referral Program</Text>
              <Switch value={referral.enabled} onValueChange={v => setReferral(p => ({ ...p, enabled: v }))} trackColor={{ true: colors.primary }} />
            </View>
            <View style={s.divider} />
            <Text style={s.fieldLabel}>Reward Type</Text>
            <View style={[s.chipRow, { marginBottom: spacing.xs }]}>
              {(['flat', 'percentage'] as const).map(type => (
                <TouchableOpacity key={type} style={[s.chip, referral.rewardType === type && s.chipActive]}
                  onPress={() => setReferral(p => ({ ...p, rewardType: type }))} activeOpacity={0.7}>
                  <Text style={[s.chipText, referral.rewardType === type && s.chipTextActive]}>
                    {type === 'flat' ? 'Fixed Amount' : 'Percentage'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <View style={s.field}>
              <Text style={s.fieldLabel}>Referred Customer Discount ({referral.rewardType === 'percentage' ? '%' : 'amount'})</Text>
              <TextInput style={s.input} value={referral.rewardValue}
                onChangeText={v => setReferral(p => ({ ...p, rewardValue: v }))}
                keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
            </View>
            <View style={s.field}>
              <Text style={s.fieldLabel}>Referrer Reward ({referral.rewardType === 'percentage' ? '%' : 'amount'})</Text>
              <TextInput style={s.input} value={referral.referrerRewardValue}
                onChangeText={v => setReferral(p => ({ ...p, referrerRewardValue: v }))}
                keyboardType="decimal-pad" placeholder="0" placeholderTextColor={colors.textLight} />
            </View>
            <View style={s.divider} />
            <View style={s.field}>
              <Text style={s.fieldLabel}>Min Order Amount (optional)</Text>
              <TextInput style={s.input} value={referral.minOrderAmount}
                onChangeText={v => setReferral(p => ({ ...p, minOrderAmount: v }))}
                keyboardType="decimal-pad" placeholder="No minimum" placeholderTextColor={colors.textLight} />
            </View>
            <View style={s.field}>
              <Text style={s.fieldLabel}>Reward Expiry (days, optional)</Text>
              <TextInput style={s.input} value={referral.expiryDays}
                onChangeText={v => setReferral(p => ({ ...p, expiryDays: v }))}
                keyboardType="number-pad" placeholder="No expiry" placeholderTextColor={colors.textLight} />
            </View>
          </View>

          {/* ── Targeting Restrictions ──────────────────────────────────── */}
          <View style={s.card}>
            <Text style={s.sectionSubLabel}>Targeting Restrictions</Text>
            <Text style={s.hint}>Limit which orders qualify for the referral reward</Text>
            <View style={s.row}>
              <View style={{ flex: 1 }}>
                <Text style={s.rowLabel}>First-Time Customers Only</Text>
                <Text style={s.hint}>Reward only fires for contacts with no prior orders</Text>
              </View>
              <Switch value={referral.firstTimeOnly} onValueChange={v => setReferral(p => ({ ...p, firstTimeOnly: v }))} trackColor={{ true: colors.primary }} />
            </View>
            <View style={s.divider} />
            <View style={s.field}>
              <Text style={s.fieldLabel}>Applies To Product Types (empty = all)</Text>
              <View style={s.chipRow}>
                {PRODUCT_TYPES.map(type => (
                  <TouchableOpacity key={type}
                    style={[s.chip, referral.appliesTo.includes(type) && s.chipActive]}
                    onPress={() => setReferral(p => ({ ...p, appliesTo: p.appliesTo.includes(type) ? p.appliesTo.filter(x => x !== type) : [...p.appliesTo, type] }))}
                    activeOpacity={0.7}>
                    <Text style={[s.chipText, referral.appliesTo.includes(type) && s.chipTextActive]}>{type.charAt(0).toUpperCase() + type.slice(1)}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={s.divider} />
            <TargetingSection
              label="Eligible Contacts / Products / Catalogues"
              value={referral.targeting}
              onChange={targeting => setReferral(p => ({ ...p, targeting }))}
            />
            <View style={s.divider} />
            {renderZoneSection(
              referral.zones,
              zones => setReferral(p => ({ ...p, zones })),
              (zoneIndex, mode) => setZonePicker({ rule: 'program', zoneIndex, mode }),
              referral.rewardType === 'percentage' ? 'Reward (%)' : 'Reward Amount',
              referral.rewardType,
            )}
          </View>

          <TouchableOpacity style={s.saveBtn} onPress={saveReferral} disabled={saving} activeOpacity={0.8}>
            {saving ? <ActivityIndicator color={colors.white} size="small" /> : <Text style={s.saveBtnText}>Save Program Settings</Text>}
          </TouchableOpacity>

        </ScrollView>
      )}

      <ConfirmModal
        visible={!!confirmModal}
        title={confirmModal?.title ?? ''} message={confirmModal?.message ?? ''}
        onConfirm={() => { confirmModal?.onConfirm(); setConfirmModal(null); }}
        onCancel={() => setConfirmModal(null)}
      />

      <ZoneMapPicker
        visible={zonePicker !== null}
        onClose={() => setZonePicker(null)}
        initialMode={zonePicker?.mode}
        initialData={zonePickerInitialData}
        onConfirm={handleZoneConfirm}
      />
    </ScreenLayout>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const getS = () => StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  scroll: { flex: 1 },
  scrollContent: { padding: spacing.md, gap: spacing.md, paddingBottom: 40 },
  card: { backgroundColor: colors.background.surface, borderRadius: 12, borderWidth: 1, borderColor: colors.border, padding: spacing.md, gap: spacing.md, ...designTokens.shadows.sm },
  divider: { height: 1, backgroundColor: colors.border },
  field: { gap: spacing.xs },
  fieldLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textLight },
  sectionSubLabel: { fontSize: typography.fontSize.sm, fontWeight: '700', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: -spacing.xs },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.sm, fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.regular, backgroundColor: colors.background.surface },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  rowLabel: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.textDark, flex: 1 },
  iconBtn: { padding: spacing.xs },
  chipRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  chip: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 20, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipActive: { borderColor: colors.primary, backgroundColor: colors.primary + '15' },
  chipText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  chipTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  saveBtn: { backgroundColor: colors.primary, borderRadius: 10, paddingVertical: spacing.sm, alignItems: 'center', justifyContent: 'center', ...designTokens.shadows.sm },
  saveBtnText: { fontSize: typography.fontSize.sm, color: colors.white, fontFamily: typography.fontFamily.semibold },
  outlineBtn: { borderRadius: 10, borderWidth: 1, borderColor: colors.border, paddingVertical: spacing.sm, alignItems: 'center' },
  outlineBtnText: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  typeBtn: { paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: 6, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  typeBtnActive: { borderColor: colors.primary, backgroundColor: colors.primary + '15' },
  typeBtnText: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  typeBtnTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  pickerBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, borderWidth: 1, borderColor: colors.primary, borderRadius: 8, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, alignSelf: 'flex-start', marginTop: spacing.xs },
  pickerBtnText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});

const getZs = () => StyleSheet.create({
  zoneCard: { borderWidth: 1, borderColor: colors.border, borderRadius: 8, padding: spacing.sm, gap: spacing.sm, backgroundColor: colors.background.primary },
  zoneName: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  zoneMeta: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  addZoneRow: { flexDirection: 'row', gap: spacing.xs, flexWrap: 'wrap' },
  addZoneBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, borderWidth: 1, borderColor: colors.primary, borderRadius: 6, paddingHorizontal: spacing.sm, paddingVertical: 4 },
  addZoneBtnText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
});
